
import { Component, Prop, Vue } from 'vue-property-decorator'
import type { FaqCategory as FaqCategoryType } from '../types'
import FaqCard from './FaqCard.vue'

/**
 * FaqCategory component for displaying a category of FAQ items
 * Manages the list of FaqCard components within a category
 * Implements accordion mode state management for mobile
 * Provides data-category-id attribute for scroll spy
 *
 * @see Requirements 7.1, 7.2
 */
@Component({
  components: {
    FaqCard,
  },
})
export default class FaqCategory extends Vue {
  // === Props ===

  /** FAQ category data containing title and items */
  @Prop({ type: Object, required: true }) readonly category!: FaqCategoryType

  /** Display index (0-based) for showing category number */
  @Prop({ type: Number, default: 0 }) readonly index!: number

  /** Whether accordion mode is enabled (mobile) */
  @Prop({ type: Boolean, default: false }) readonly accordionMode!: boolean

  /** Fixed header height offset for mobile scroll calculation */
  @Prop({ type: Number, default: 0 }) readonly scrollOffset!: number

  // === Data ===

  /** Currently expanded card ID in accordion mode */
  private expandedCardId = ''

  // === Methods ===

  /**
   * Check if a specific card is expanded
   * Used to control expanded state in accordion mode
   * @param itemId - The FAQ item ID to check
   * @returns Whether the card is expanded
   */
  isCardExpanded(itemId: string): boolean {
    if (this.accordionMode) {
      // In accordion mode, only the expandedCardId is expanded
      return this.expandedCardId === itemId
    }
    // In normal mode, FaqCard manages its own state
    return false
  }

  /**
   * Handle card toggle event from FaqCard
   * Manages accordion behavior when enabled
   * @param itemId - The FAQ item ID that was toggled
   * @param isExpanded - The new expanded state
   */
  handleCardToggle(itemId: string, isExpanded: boolean): void {
    if (this.accordionMode) {
      if (isExpanded) {
        // Card is being expanded, set it as the expanded card
        // This will automatically collapse other cards
        this.expandedCardId = itemId
      } else if (this.expandedCardId === itemId) {
        // The currently expanded card is being collapsed
        this.expandedCardId = ''
      }
    }

    // Emit toggle event to parent for potential use
    this.$emit('card-toggle', this.category.id, itemId, isExpanded)
  }
}
